import React from "react";
import * as Sentry from "@sentry/react";
import { Box, Button, Typography, Container } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

interface ErrorBoundaryFallbackProps {
  error: Error;
  resetError: () => void;
}

/**
 * Fallback UI component displayed when an error is caught by the Error Boundary.
 * Provides a user-friendly error message and option to reload the page.
 */
const ErrorBoundaryFallback: React.FC<ErrorBoundaryFallbackProps> = ({
  error,
  resetError,
}) => {
  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          textAlign: "center",
          gap: 3,
        }}
      >
        <ErrorOutlineIcon sx={{ fontSize: 80, color: "error.main" }} />

        <Typography variant="h4" component="h1" gutterBottom>
          Something went wrong
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          We're sorry for the inconvenience. An error has occurred and has been
          reported to our team.
        </Typography>

        {import.meta.env.DEV && (
          <Box
            sx={{
              p: 2,
              bgcolor: "background.paper",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
              textAlign: "left",
              width: "100%",
              overflow: "auto",
            }}
          >
            <Typography variant="subtitle2" color="error" gutterBottom>
              Error Details (Dev Only):
            </Typography>
            <Typography
              variant="body2"
              component="pre"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.875rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {error.message}
              {"\n\n"}
              {error.stack}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            size="large"
          >
            Reload Page
          </Button>

          <Button variant="outlined" onClick={resetError} size="large">
            Try Again
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

/**
 * Sentry Error Boundary wrapper component.
 *
 * This component catches React errors in the component tree below it
 * and reports them to Sentry. It also displays a fallback UI when an error occurs.
 *
 * Usage:
 * ```tsx
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 * ```
 */
export const ErrorBoundary = Sentry.withErrorBoundary(
  ({ children }: { children: React.ReactNode }) => <>{children}</>,
  {
    fallback: (props) => (
      <ErrorBoundaryFallback
        error={props.error as Error}
        resetError={props.resetError}
      />
    ),
    showDialog: false,
  },
);
